import { TextField } from "@mui/material";
import AutoComplete from "components/Common/Autocomplete/Autocomplete";
import { TruckInfoAutocompletes, TruckInfoWrapper } from "./TruckInfo.styles";
import { getFormFieldProps } from "utils/form";
import { Controller, useFormContext } from "react-hook-form";
import { schema } from "../stepOneSchema";
import { useEffect, useState } from "react";
import { models } from "types/api/viewModels";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "stores/application.store";
import { AllFieldsRequiredText, SelectAccountText, TruckInfoText, TruckNameText } from "constants/text";
import { truckActions } from "features/reducers/truck/truck";
import { SelectProductLine } from "components/NewOrders/constants";

export interface TruckInfoProps {
	accounts: models["CustomerAccountViewModel"][] | null;
	isAccountsLoading: boolean;
	setSelectedAccountNumber: (accountNumber: models["CustomerAccountViewModel"] | undefined) => void;
}

const TruckInfo = ({ accounts, isAccountsLoading, setSelectedAccountNumber }: TruckInfoProps) => {
	const { formState, register, clearErrors, control, watch, setValue, trigger, getValues } = useFormContext();
	const formFieldData = { formState, register, schema };

	const [productLines, setProductLines] = useState<models["ProductLineViewModel"][]>();

	const dispatch = useDispatch();
	const truck = useSelector((state: RootState) => state.truck.truck);

	const mappedProductLines = (account: models["CustomerAccountViewModel"] | undefined) =>
		account?.truckCapacities
			?.map((truckCapacity: models["TruckCapacityViewModel"]) => truckCapacity.productLine)
			.filter(
				(productLine): productLine is models["ProductLineViewModel"] =>
					productLine !== undefined && productLine !== null
			);

	useEffect(() => {
		if (!isAccountsLoading) {
			const foundAccount = accounts?.find((account) => account.accountId === truck?.accountId);
			setValue("accountNumber", foundAccount);
			setProductLines(mappedProductLines(foundAccount));
			setValue("productLine", truck?.productLine);
			setSelectedAccountNumber(foundAccount);
		}
		if (!getValues("truckName")) setValue("truckName", truck?.truckName);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAccountsLoading]); // only run on mount to set the form's initial values, after accounts load

	return (
		<TruckInfoWrapper>
			<span>{TruckInfoText}</span>
			<span>{AllFieldsRequiredText}</span>
			<TruckInfoAutocompletes>
				<Controller
					control={control}
					name="accountNumber"
					render={({ field: { onChange, value = null }, fieldState: { error } }) => (
						<AutoComplete
							options={
								accounts?.filter(
									(account) =>
										account.isActive &&
										account.truckCapacities?.length &&
										account.truckCapacities?.length > 0
								) ?? []
							}
							onChange={(_, value) => {
								onChange(value);
								setProductLines(mappedProductLines(value));
								setValue("productLine", null);
								setValue("stops", [{ address: null }]);
								if (value === null) {
									clearErrors("productLine");
									clearErrors("stops");
								} else if (Object.keys(formState.errors).length > 0) {
									trigger("productLine");
									trigger("stops");
								} else {
									dispatch(truckActions.updateSelectedAccount(value));
									setSelectedAccountNumber(value);
								}
							}}
							getOptionLabel={(option: models["CustomerAccountViewModel"]) =>
								[option.accountNumber, option.accountName].filter(Boolean).join(" - ")
							}
							errorText={error?.message}
							isError={!!error}
							label={SelectAccountText}
							value={value}
							dataTestId="truck-info-accountNumber"
							isLoading={isAccountsLoading}
							required
						/>
					)}
				/>
				<Controller
					control={control}
					name="productLine"
					render={({ field: { onChange, value = null }, fieldState: { error } }) => (
						<AutoComplete
							options={productLines ?? []}
							onChange={(_, value) => {
								onChange(value);
							}}
							getOptionLabel={(option: models["ProductLineViewModel"]) =>
								option.description ?? option.code ?? ""
							}
							errorText={error?.message}
							isError={!!error}
							label={SelectProductLine}
							value={value}
							dataTestId="truck-info-productLine"
							disabled={!watch("accountNumber")}
							required
							isLoading={isAccountsLoading}
						/>
					)}
				/>
			</TruckInfoAutocompletes>
			<div>
				<TextField
					label={TruckNameText}
					size="small"
					{...getFormFieldProps({
						name: "truckName",
						characterLimit: {
							currentLength: watch("truckName")?.length || 0,
							limit: 15
						},
						...formFieldData
					})}
					data-testid="truck-info-truckName"
					fullWidth
					onChange={(e) => {
						setValue("truckName", e.target.value);
						clearErrors("truckName");
					}}
				/>
			</div>
		</TruckInfoWrapper>
	);
};

export default TruckInfo;
